import { arredondar, formatarPercentual, formatarReais } from './texto.ts';

/**
 * Ajustes que entram depois da premiação calculada: proporcionalidade por dias
 * trabalhados e desconto por faltas.
 *
 * Este módulo é PURO de propósito — sem banco, sem rede, sem data de hoje. Ele é
 * importado pelo servidor e pela tela, para que o número que muda enquanto o
 * usuário digita seja produzido pelo mesmo código que grava o fechamento.
 *
 * Não é preciosismo: neste projeto já apareceram três implementações da mesma
 * regra dentro do VBA (uma delas errada), e um seed que discordava do motor no
 * formato dos parâmetros. Regra escrita duas vezes é regra que vai divergir.
 */

export type TipoAjuste = 'proporcionalidade' | 'falta_injustificada' | 'falta_justificada';

export interface RegrasDeAjuste {
  /**
   * A ordem em que os três se aplicam, e ela muda o resultado: tirar 1/3 de
   * R$ 787,50 e depois descontar R$ 80 dá R$ 445,00; descontar os R$ 80 antes
   * dá R$ 471,67. Fica na configuração para que rever a política não vire
   * alteração de código.
   */
  ordem: TipoAjuste[];

  proporcionalidade: {
    /** `dias_do_mes` acompanha 28, 29, 30 ou 31. `fixo` usa sempre `diasFixos`. */
    base: 'dias_do_mes' | 'fixo';
    diasFixos: number;
    /** Impede que informar mais dias que a base pague acima de 100%. */
    limitarEmCemPorCento: boolean;
    /**
     * Se a falta não justificada também tira o dia do total trabalhado.
     *
     * Ligado, ela pesa duas vezes — pela proporcionalidade e pelo fator — e é
     * essa a política combinada. Cada lançamento pode divergir deste padrão
     * pelo campo `descontarFaltasDosDias`, para o caso em que a chefia decide
     * que aquela falta específica não deve reduzir o dia.
     */
    descontarFaltasInjustificadasPorPadrao: boolean;
  };

  faltaInjustificada: {
    /** Cada falta tira 1/divisor da premiação. Com 3, cada falta tira um terço. */
    divisorPorFalta: number;
    /** A partir desta quantidade, zera. */
    faltasParaZerar: number;
  };

  faltaJustificada: {
    /** Valor do dia = salário ÷ divisor. */
    divisorDoSalario: number;
    /** Quantos dias de salário cada falta desconta. */
    diasPorFalta: number;
  };

  /** Impede que a premiação fique negativa: não paga, mas também não vira dívida. */
  pisoEmZero: boolean;
}

export interface LancamentoDeAjuste {
  /** Nulo = trabalhou o mês inteiro. Só se preenche para reduzir. */
  diasTrabalhados: number | null;
  faltasJustificadas: number;
  faltasInjustificadas: number;
  /** Salário vigente na competência. Nulo = não cadastrado. */
  salario: number | null;
  /**
   * Se as faltas não justificadas desta pessoa tiram dias do total trabalhado.
   * Nulo = segue o padrão da regra.
   */
  descontarFaltasDosDias: boolean | null;
  /**
   * Correção em reais lançada à mão, somada no fim. Positiva acrescenta,
   * negativa desconta.
   *
   * Entra por último e fora da `ordem` configurável de propósito: a ordem
   * existe porque fator e desconto em reais dão resultados diferentes conforme
   * a sequência, e isso é regra. O ajuste manual não é regra — é alguém
   * dizendo "neste caso o número certo é outro". Aplicá-lo antes faria uma
   * regra incidir sobre uma decisão humana.
   */
  ajusteManual: number;
  /** Por que o ajuste foi feito. Obrigatório quando há ajuste. */
  ajusteManualMotivo: string | null;
}

export interface EtapaDeAjuste {
  tipo: TipoAjuste | 'ajuste_manual';
  descricao: string;
  valorAntes: number;
  valorDepois: number;
}

export interface ResultadoDosAjustes {
  valorFinal: number;
  /** A conta passo a passo, na ordem aplicada, para o memorial. */
  etapas: EtapaDeAjuste[];
  fatorFaltas: number;
  descontoFaltas: number;
  valorDoDia: number | null;
  diasBase: number;
  avisos: string[];
}

export const LANCAMENTO_VAZIO: LancamentoDeAjuste = {
  diasTrabalhados: null,
  faltasJustificadas: 0,
  faltasInjustificadas: 0,
  salario: null,
  descontarFaltasDosDias: null,
  ajusteManual: 0,
  ajusteManualMotivo: null,
};

/** Quantos dias tem a competência `AAAA-MM`. */
export function diasDoMes(competencia: string): number {
  const [ano, mes] = competencia.split('-').map(Number);
  if (ano === undefined || mes === undefined || Number.isNaN(ano) || Number.isNaN(mes)) return 30;
  // Dia 0 do mês seguinte é o último dia deste.
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

/**
 * Aplica os ajustes sobre a premiação já calculada.
 *
 * `premiacao` é o valor depois do balizador. O retorno traz o valor final e
 * cada etapa da conta, porque é isso que responde a pessoa que contesta.
 */
export function aplicarAjustes(
  premiacao: number,
  lancamento: LancamentoDeAjuste,
  regras: RegrasDeAjuste,
  competencia: string,
): ResultadoDosAjustes {
  const etapas: EtapaDeAjuste[] = [];
  const avisos: string[] = [];

  const diasBase =
    regras.proporcionalidade.base === 'dias_do_mes'
      ? diasDoMes(competencia)
      : regras.proporcionalidade.diasFixos;

  const valorDoDia =
    lancamento.salario === null || regras.faltaJustificada.divisorDoSalario <= 0
      ? null
      : arredondar(lancamento.salario / regras.faltaJustificada.divisorDoSalario, 2);

  let fatorFaltas = 1;
  let descontoFaltas = 0;
  let valor = arredondar(premiacao, 2);

  const registrar = (tipo: TipoAjuste, descricao: string, depois: number): void => {
    const valorDepois = arredondar(regras.pisoEmZero ? Math.max(0, depois) : depois, 2);
    etapas.push({ tipo, descricao, valorAntes: valor, valorDepois });
    valor = valorDepois;
  };

  for (const tipo of regras.ordem) {
    if (valor === 0 && regras.pisoEmZero) break; // já zerou, o resto não muda nada

    if (tipo === 'proporcionalidade') {
      const descontarFaltas =
        lancamento.descontarFaltasDosDias ??
        regras.proporcionalidade.descontarFaltasInjustificadasPorPadrao;
      const faltasQueTiramDia = descontarFaltas ? lancamento.faltasInjustificadas : 0;

      // Campo em branco significa mês cheio. Só há proporcionalidade se alguém
      // reduziu os dias à mão ou se as faltas descontam dia.
      if (lancamento.diasTrabalhados === null && faltasQueTiramDia === 0) continue;

      if (diasBase <= 0) {
        avisos.push('A base de dias do mês está zerada na configuração — proporcionalidade ignorada.');
        continue;
      }

      const informados = lancamento.diasTrabalhados ?? diasBase;
      const dias = Math.max(0, informados - faltasQueTiramDia);
      const bruta = dias / diasBase;
      const proporcao = regras.proporcionalidade.limitarEmCemPorCento ? Math.min(1, bruta) : bruta;
      if (bruta > 1 && regras.proporcionalidade.limitarEmCemPorCento) {
        avisos.push(
          `Foram informados ${dias} dias trabalhados num mês de ${diasBase} — a proporção foi limitada a 100%.`,
        );
      }

      const origem =
        faltasQueTiramDia === 0
          ? ''
          : lancamento.diasTrabalhados === null
            ? ` (${diasBase} do mês menos ${faltasQueTiramDia} falta(s) não justificada(s))`
            : ` (${informados} informados menos ${faltasQueTiramDia} falta(s) não justificada(s))`;

      registrar(
        tipo,
        `Trabalhou ${dias} de ${diasBase} dias${origem} = ${formatarPercentual(proporcao)}: ` +
          `${formatarReais(valor)} ÷ ${diasBase} × ${dias}`,
        valor * proporcao,
      );
      continue;
    }

    if (tipo === 'falta_injustificada') {
      const faltas = lancamento.faltasInjustificadas;
      if (faltas <= 0) continue;

      const zera = faltas >= regras.faltaInjustificada.faltasParaZerar;
      const divisor = regras.faltaInjustificada.divisorPorFalta;
      fatorFaltas = zera || divisor <= 0 ? 0 : Math.max(0, 1 - faltas / divisor);

      registrar(
        tipo,
        zera
          ? `${faltas} falta(s) não justificada(s): a partir de ${regras.faltaInjustificada.faltasParaZerar} a premiação é zerada`
          : `${faltas} falta(s) não justificada(s): perde ${faltas}/${divisor} da premiação, resta ${formatarPercentual(fatorFaltas)}`,
        valor * fatorFaltas,
      );
      continue;
    }

    if (tipo === 'falta_justificada') {
      const faltas = lancamento.faltasJustificadas;
      if (faltas <= 0) continue;

      if (valorDoDia === null) {
        // Sem salário não dá para calcular o desconto. Avisa alto em vez de
        // deixar passar: silêncio aqui é premiação paga a mais.
        avisos.push(
          `${faltas} falta(s) justificada(s) sem desconto: falta cadastrar o salário desta pessoa.`,
        );
        continue;
      }

      descontoFaltas = arredondar(valorDoDia * regras.faltaJustificada.diasPorFalta * faltas, 2);
      registrar(
        tipo,
        `${faltas} falta(s) justificada(s): ${formatarReais(valorDoDia)} por dia × ` +
          `${regras.faltaJustificada.diasPorFalta} dia(s) × ${faltas} = −${formatarReais(descontoFaltas)}`,
        valor - descontoFaltas,
      );
    }
  }

  // Depois de tudo: a correção lançada à mão.
  //
  // O piso em zero vale também aqui — um desconto maior que o valor para em
  // zero, não vira dívida. Já para cima não há teto: quem corrigiu para mais
  // sabe o que está fazendo, e o motivo fica gravado do lado.
  if (lancamento.ajusteManual !== 0) {
    const antes = valor;
    const bruto = antes + lancamento.ajusteManual;
    const depois = arredondar(regras.pisoEmZero ? Math.max(0, bruto) : bruto, 2);

    etapas.push({
      tipo: 'ajuste_manual',
      descricao:
        `Ajuste manual de ${formatarReais(lancamento.ajusteManual)}` +
        (lancamento.ajusteManualMotivo === null || lancamento.ajusteManualMotivo === ''
          ? ''
          : `: ${lancamento.ajusteManualMotivo}`),
      valorAntes: antes,
      valorDepois: depois,
    });
    valor = depois;
  }

  return {
    valorFinal: valor,
    etapas,
    fatorFaltas,
    descontoFaltas,
    valorDoDia,
    diasBase,
    avisos,
  };
}
